import { useMutation } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import {
  ALIGNMENT_NO_RESPONSES_CODE,
  AlignmentExportError,
  downloadAlignmentExport,
} from './download-alignment-export';

/**
 * DEV-268 — "Exportar alinhamento" com os QUATRO Sonners do card (AC-15..18),
 * no molde de `useExportProgramBase` (EU-654/739): um único toast por
 * programa mudando de estado —
 *   "A planilha está sendo preparada" (loading, sem Fechar)
 *   → "Planilha baixada com sucesso" (Fechar, 6 s)
 *   | "Formulário ainda sem respostas" (Fechar, 6 s; 409 `alignment_no_responses`)
 *   | "Algo deu errado. Tente novamente" (Fechar, 6 s).
 * Dois Sonners nunca ficam na tela ao mesmo tempo (mesmo `id`).
 *
 * AC-20 (retorno da QA, item 1.1): o disparo repetido do mesmo programa é
 * barrado de forma SÍNCRONA, antes do `mutate` — o `onMutate` do TanStack só
 * roda depois de um `await`, então três cliques em sequência passavam todos
 * pelo registro de pendência e viravam três requests, três downloads e um
 * toast de preparação que nunca saía. Agora: um request, um download, e o
 * toast final SEMPRE substitui o de preparação (`onSettled`).
 */
export const ALIGNMENT_EXPORT_TOASTS = {
  preparing: 'A planilha está sendo preparada',
  success: 'Planilha baixada com sucesso',
  empty: 'Formulário ainda sem respostas',
  error: 'Algo deu errado. Tente novamente',
} as const;

/** Figma: Sonners com "Fechar" duram 6 s ou até a pessoa fechar. */
export const ALIGNMENT_TOAST_DURATION_MS = 6_000;

/** Ver `use-export-program-base.ts`: o merge do sonner mantém `type: 'loading'`. */
const RESET_LOADING_TYPE = { type: undefined };

/** Programas com preparação em andamento (módulo: vale para qualquer instância do hook). */
const pendingPrograms = new Set<string>();

export function alignmentExportToastId(programId: string): string {
  return `alignment-export-${programId}`;
}

/**
 * AC-15 (retorno da QA, item 1.2): o sonner atualiza um toast existente por
 * id fazendo MERGE das opções — se o toast final anterior ("Fechar") ainda
 * está na tela, o loading herdaria a `action`. `action`/`cancel` explícitos
 * como `undefined` sobrescrevem no merge: o toast de preparação NUNCA tem
 * botão. (`toast.dismiss` antes não serve: ele é assíncrono — rAF — e
 * apagaria o loading recém-criado.)
 */
function preparingToast(toastId: string) {
  toast.loading(ALIGNMENT_EXPORT_TOASTS.preparing, {
    id: toastId,
    duration: Infinity,
    action: undefined,
    cancel: undefined,
  });
}

function finalToast(toastId: string, message: string) {
  toast(message, {
    id: toastId,
    duration: ALIGNMENT_TOAST_DURATION_MS,
    action: { label: 'Fechar', onClick: () => toast.dismiss(toastId) },
    ...RESET_LOADING_TYPE,
  });
}

function finalMessage(err: unknown): string {
  if (!err) return ALIGNMENT_EXPORT_TOASTS.success;
  const empty =
    err instanceof AlignmentExportError &&
    err.status === 409 &&
    err.code === ALIGNMENT_NO_RESPONSES_CODE;
  return empty ? ALIGNMENT_EXPORT_TOASTS.empty : ALIGNMENT_EXPORT_TOASTS.error;
}

export function useExportAlignment() {
  const mutation = useMutation({
    mutationFn: async (input: { programId: string }) => downloadAlignmentExport(input.programId),
    // Único ponto de saída: libera o programa e troca o toast de preparação
    // pelo final (sucesso | sem respostas | erro), aconteça o que acontecer.
    onSettled: (_data, err, input) => {
      pendingPrograms.delete(input.programId);
      finalToast(alignmentExportToastId(input.programId), finalMessage(err));
    },
  });

  return {
    ...mutation,
    /** AC-20 — ignora o disparo redundante do mesmo programa (guarda síncrona). */
    mutate: (input: { programId: string }) => {
      if (pendingPrograms.has(input.programId)) return;
      pendingPrograms.add(input.programId);
      preparingToast(alignmentExportToastId(input.programId));
      mutation.mutate(input);
    },
  };
}
